import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, switchMap, tap } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { userFeature } from '@state/user';
import { MsisdnService } from '@core/services';
import { SubscriptionActions } from '@state/subscription/subscription.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { REQUEST_DATE_FORMAT } from '@core/const/const';
import dayjs from 'dayjs';
import { saveAs } from '@core/helpers';

@Injectable()
export class SubscriptionEffects {
  loadCurrentSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getCurrentSubscription),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getCurrentSubscription(msisdn).pipe(
          map((subscription) => SubscriptionActions.getCurrentSubscriptionSuccess(subscription)),
          catchError(() => of(SubscriptionActions.getCurrentSubscriptionFailure()))
        )
      )
    );
  });
  loadCurrentContract$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getContractDetails),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getContract(msisdn).pipe(
          map((subscription) => SubscriptionActions.getContractDetailsSuccess(subscription)),
          catchError(() => of(SubscriptionActions.getContractDetailsFailure()))
        )
      )
    );
  });
  loadCurrentCosts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getCostsDetails),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getCosts(msisdn).pipe(
          map((subscription) => {
            const totalPrice = subscription.details.reduce(
              (total, detail) => total + detail.price,
              0
            );
            return SubscriptionActions.getCostsDetailsSuccess(subscription.details, totalPrice);
          }),
          catchError(() => of(SubscriptionActions.getCostsDetailsFailure()))
        )
      )
    );
  });
  loadInternetUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getInternetUsageDetails),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      distinctUntilChanged(
        ([prevAction, prevMsisdn], [currAction, currMsisdn]) =>
          prevAction.monthIndex === currAction.monthIndex &&
          prevAction.isOnlyExtra === currAction.isOnlyExtra &&
          prevMsisdn === currMsisdn
      ),
      switchMap(([action, msisdn]) => {
        const from = dayjs().subtract(action.monthIndex, 'month').startOf('month').format(REQUEST_DATE_FORMAT);
        const to = dayjs().subtract(action.monthIndex, 'month').endOf('month').format(REQUEST_DATE_FORMAT);
        const isOnlyExtraCosts = action.isOnlyExtra;
        return this.msisdnService.getInternetUsageList(msisdn, from, to, isOnlyExtraCosts).pipe(
          map((internetUsageLike) => {
            const usageList = internetUsageLike.details.map((usageLike) => ({
              date: new Date(usageLike.date),
              country: usageLike.country,
              bytesAmount: usageLike.usage,
              extraPrice: usageLike.extraCosts,
            }));

            return SubscriptionActions.getInternetUsageDetailsSuccess({
              list: usageList,
              total: internetUsageLike.total,
              extraPrice: internetUsageLike.cost,
            });
          }),
          catchError(() => of(SubscriptionActions.getInternetUsageDetailsFailure()))
        );
      })
    );
  });
  loadCallSmsUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getCallSmsUsageDetails),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      distinctUntilChanged(
        ([prevAction, prevMsisdn], [currAction, currMsisdn]) =>
          prevAction.monthIndex === currAction.monthIndex &&
          prevAction.isOnlyExtra === currAction.isOnlyExtra &&
          prevMsisdn === currMsisdn
      ),
      switchMap(([action, msisdn]) => {
        const from = dayjs().subtract(action.monthIndex, 'month').startOf('month').format(REQUEST_DATE_FORMAT);
        const to = dayjs().subtract(action.monthIndex, 'month').endOf('month').format(REQUEST_DATE_FORMAT);
        const isOnlyExtraCosts = action.isOnlyExtra;
        return this.msisdnService.getCallSmsUsageList(msisdn, from, to, isOnlyExtraCosts).pipe(
          map((callSmsUsageLike) => {
            const usageList = callSmsUsageLike.details.map((usageLike) => ({
              dateTime: new Date(usageLike.date),
              phoneNumber: usageLike.number,
              secondsAmount: usageLike.usage,
              extraPrice: usageLike.extraCosts,
            }));

            return SubscriptionActions.getCallSmsUsageDetailsSuccess({
              list: usageList,
              total: callSmsUsageLike.total,
              extraPrice: callSmsUsageLike.cost,
            });
          }),
          catchError(() => of(SubscriptionActions.getCallSmsUsageDetailsFailure()))
        );
      })
    );
  });
  loadBundles = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getBundles),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getCurrentBundlesList(msisdn).pipe(
          map((payload) => SubscriptionActions.getBundlesSuccess(payload)),
          catchError(() => of(SubscriptionActions.getBundlesFailure()))
        )
      )
    );
  });
  loadWallet = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getWallet),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getWallet(msisdn).pipe(
          map((payload) => SubscriptionActions.getWalletSuccess(payload)),
          catchError(() => of(SubscriptionActions.getWalletFailure()))
        )
      )
    );
  });
  topUpWallet = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.topupWallet),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ amount }, msisdn]) =>
        this.msisdnService.topUpWallet(msisdn, amount).pipe(
          map((response) => response.url),
          tap((url) => (window.location.href = url)),
          map((url) => SubscriptionActions.topupWalletSuccess(url)),
          catchError(() => of(SubscriptionActions.topupWalletFailure()))
        )
      )
    );
  });

  topUpExtraBundles = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.topupExtraBundle),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ bundleId, isRoaming }, msisdn]) =>
        this.msisdnService.topUpExtraBundle(bundleId, msisdn, isRoaming).pipe(
          map((response) => {
            window.location.href = response.url;
            return SubscriptionActions.topupExtraBundleSuccess(bundleId, response.url);
          }),
          catchError(() => of(SubscriptionActions.topupExtraBundleFailure()))
        )
      )
    );
  });

  loadWalletInternetUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getWalletInternetUsage),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) => {
        const from = dayjs(action.from).format(REQUEST_DATE_FORMAT);
        const to = dayjs(action.to).format(REQUEST_DATE_FORMAT);
        const { sortType } = action;

        return this.msisdnService.getWalletInternetUsage(msisdn, from, to, sortType).pipe(
          map((usage) => SubscriptionActions.getWalletInternetUsageSuccess(usage)),
          catchError(() => of(SubscriptionActions.getWalletInternetUsageFailure()))
        );
      })
    );
  });
  loadWalletCallSmsUsage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.getWalletVoiceUsage),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) => {
        const from = dayjs(action.from).format(REQUEST_DATE_FORMAT);
        const to = dayjs(action.to).format(REQUEST_DATE_FORMAT);
        const { sortType } = action;

        return this.msisdnService.getWalletCallSmsUsage(msisdn, from, to, sortType).pipe(
          map((usage) => SubscriptionActions.getWalletVoiceUsageSuccess(usage)),
          catchError(() => of(SubscriptionActions.getWalletVoiceUsageFailure()))
        );
      })
    );
  });
  updateSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.updateSubscription),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) => {
        return this.msisdnService.updateSubscription(msisdn, action.body).pipe(
          map(() => SubscriptionActions.updateSubscriptionSuccess()),
          catchError(() => of(SubscriptionActions.updateSubscriptionFailure()))
        );
      })
    );
  });
  downloadUsageOverview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.downloadUsageOverview),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) => {
        const from = dayjs(action.from).format(REQUEST_DATE_FORMAT);
        const to = dayjs(action.to).format(REQUEST_DATE_FORMAT);

        return this.msisdnService.getUsageOverview(msisdn, from, to, action.isToggleOn).pipe(
          tap((response) => saveAs(response, `${msisdn}_usage.pdf`)),
          map(() => SubscriptionActions.downloadUsageOverviewSuccess()),
          catchError(() => of(SubscriptionActions.downloadUsageOverviewFailure()))
        );
      })
    );
  });
  downloadWalletUsageOverview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.downloadWalletUsageOverview),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) => {
        const from = dayjs(action.from).format(REQUEST_DATE_FORMAT);
        const to = dayjs(action.to).format(REQUEST_DATE_FORMAT);

        return this.msisdnService.getWalletUsageOverview(msisdn, from, to).pipe(
          tap((response) => saveAs(response, `${msisdn}_wallet_usage.pdf`)),
          map(() => SubscriptionActions.downloadWalletUsageOverviewSuccess()),
          catchError(() => of(SubscriptionActions.downloadWalletUsageOverviewFailure()))
        );
      })
    );
  });
  downloadContract$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.downloadContract),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getContractDetails(msisdn).pipe(
          tap((response) => saveAs(response, `${msisdn}_contract.pdf`)),
          map(() => SubscriptionActions.downloadContractSuccess()),
          catchError((err) => {
            return of(SubscriptionActions.downloadContractFailure());
          })
        )
      )
    );
  });
  downloadCosts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscriptionActions.downloadCosts),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getCostsPdf(msisdn).pipe(
          tap((response) => saveAs(response, `${msisdn}_costs.pdf`)),
          map(() => SubscriptionActions.downloadCostsSuccess()),
          catchError(() => of(SubscriptionActions.downloadCostsFailure()))
        )
      )
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly msisdnService: MsisdnService
  ) {}
}
